import { api } from '@/api/lunchClient'
import type { AuthDriver } from '@/auth'
import { isLocal } from '@/environments'
import { User } from '@/models'
import { keycloakInitOptions } from '@/plugins/keycloak'
import { useSettingsStore } from '@/stores'
import { useAuthStore } from '@/stores/auth'
import { useTokenStore } from '@/stores/token'

const KeycloakDriver: AuthDriver = {
  refresh(): Promise<void> {
    console.log('refresh')
  },

  async login(): Promise<void> {
    console.log('login')
  },

  async proxyLogin(): Promise<void> {
    console.log('login')
  },

  async logout(): Promise<void> {
    const token = useTokenStore()
    const keycloak = token.getClient()

    const auth = useAuthStore()
    const settings = useSettingsStore()
    auth.setLoading(true)

    await settings.teapot()

    keycloak.authenticated = false
    auth.clear()
    token.clear()
    keycloak.logout()

    auth.setInitalized(false)
  },

  async session(): Promise<User | null> {
    const token = useTokenStore()
    const auth = useAuthStore()

    const keycloak = token.getClient()
    auth.setLoading(true)
    auth.setInitalized(true)

    // Local development only. `keycloakInitOptions` uses `onLoad: 'login-required'`, so
    // without a live Keycloak session the SPA navigates the whole page to the identity
    // provider -- which makes it impossible to run the app against a local API, and
    // impossible to drive it as a seeded test user. When a bearer token has already been
    // put in the token store, trust it and resolve the user straight from the API instead.
    // `isLocal` is `import.meta.env.DEV`, which Vite statically replaces with `false` when
    // building, so this branch constant-folds away and cannot run in a deployed bundle.
    //
    // Deliberately uses the lunch client rather than `User.current()`: the model stack in
    // plugins/http.ts resolves its base URL from the settings product first and only falls
    // back to VITE_APP_HOST_API, so against a local API it queries the deployed host, gets
    // nothing back, and the app falls through to the Keycloak redirect anyway.
    if (isLocal && token.access) {
      try {
        const response = await api.get<{ data: User[] }>('/v0/users?filters[current]=true&include=roles')
        const developmentUser = response.data?.[0]

        if (developmentUser) {
          auth.setUser(developmentUser)
          auth.setLoading(false)

          return auth.getUser()
        }
      } catch (error) {
        // Fall through to the normal Keycloak flow -- a bad or stale local token should
        // not be a dead end.
        console.warn('[dev] Local bearer token could not be resolved to a user.', error)
      }
    }

    if (!keycloak.didInitialize) {
      try {
        await keycloak.init(keycloakInitOptions)

        if (keycloak.authenticated) {
          token.setAccess(keycloak.token || undefined)
          token.setRefresh(keycloak.refreshToken || undefined)

          // Use the token store's setInterval/clearInterval wrappers rather than a raw
          // setInterval so the interval id is captured and logout() (via token.clear())
          // can actually stop it. A raw setInterval here would survive logout and keep
          // firing, forcing an unexpected re-login on refresh failure.
          token.setInterval(async () => {
            try {
              await keycloak.updateToken(120)
              token.setAccess(keycloak.token || undefined)
              token.setRefresh(keycloak.refreshToken || undefined)
            } catch (error) {
              console.error('Keycloak Token Refresh Failed:', error)
              await keycloak.login()
            }
          }, 30_000)

          const user = await User.current()
          if (user) {
            auth.setUser(user)
            keycloak.authenticated = true
          }
        }
      } catch (error) {
        console.error('Keycloak Initialization Failed:', error)
      }
    }

    if (keycloak.didInitialize && !keycloak.authenticated) {
      await keycloak.login()
    }

    auth.setLoading(false)
    return auth.getUser()
  },
}

export default KeycloakDriver
